import { ref } from 'vue';
import { useBulkDelete } from '@/shared/lib/use';
import { trainingApi, type TrainingListItem } from '@/entities/Training';

export interface UseTrainingListDeleteOptions {
  selectedIds: () => number[];
  removeFromSelection: (ids: number[]) => void;
  reloadTrainings: () => void;
}

export function useTrainingListDelete({
  selectedIds,
  removeFromSelection,
  reloadTrainings,
}: UseTrainingListDeleteOptions) {
  const pendingSingleId = ref<number | null>(null);

  const deletionIds = (): number[] =>
    pendingSingleId.value !== null ? [pendingSingleId.value] : selectedIds();

  const { openDeleteConfirmationModal } = useBulkDelete<TrainingListItem>({
    entityName: { singular: 'Treinamento', plural: 'treinamentos' },
    successMessage: 'Treinamento deletado com sucesso',
    successMessagePlural: 'Treinamentos deletados com sucesso',
    errorMessage: 'Erro ao deletar Treinamento. Tente novamente',
    errorMessagePlural: 'Erro ao deletar Treinamentos. Tente novamente',
    getConfirmTitle: (count) =>
      count <= 1
        ? 'Tem certeza que deseja deletar o Treinamento?'
        : `Tem certeza que deseja deletar ${count} treinamentos?`,
    singleDeleteApi: (id) => trainingApi.deleteTraining(id),
    bulkDeleteApi: (ids) => trainingApi.bulkDeleteTrainings(ids),
    onDeleteSuccess: () => {
      pendingSingleId.value = null;
      reloadTrainings();
    },
    onStaleData: () => {
      pendingSingleId.value = null;
      reloadTrainings();
    },
    removeFromSelection,
    selectedCount: () => deletionIds().length,
    selectedIds: deletionIds,
    onModalClose: () => {
      pendingSingleId.value = null;
    },
  });

  function requestSingleDelete(id: number): void {
    pendingSingleId.value = id;
    void openDeleteConfirmationModal();
  }

  function requestBulkDelete(): void {
    pendingSingleId.value = null;
    void openDeleteConfirmationModal();
  }

  return {
    requestSingleDelete,
    requestBulkDelete,
  };
}
